import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, mergeProps as _mergeProps, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-b17ce672"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "periodontal-chart__content" }
const _hoisted_2 = ["innerHTML"]

import { computed, getCurrentInstance, watch } from 'vue'
import { Chart, registerables } from 'chart.js'
import { LineChart, useLineChart } from 'vue-chart-3'
import { DOWN_TEETH, UP_TEETH } from '@/shared/constants'
import { PeriodontalDiagramType, TeethType } from '@/shared/types'
import { useRoute } from 'vue-router'

interface Props {
  data: PeriodontalDiagramType
  previousData: PeriodontalDiagramType
  isReverse?: boolean
  isUpperTeeth: boolean
  isPdfMode: boolean
  isInteractive: boolean
}


export default /*#__PURE__*/_defineComponent({
  props: {
    data: null,
    previousData: null,
    isReverse: { type: Boolean },
    isUpperTeeth: { type: Boolean },
    isPdfMode: { type: Boolean },
    isInteractive: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  data: PeriodontalDiagramType
  previousData: PeriodontalDiagramType
  isReverse?: boolean
  isUpperTeeth: boolean
  isPdfMode: boolean
  isInteractive: boolean
}


const route = useRoute()

const isOverlay = computed(() => route.query['overlay-mode'] === '1')
const isPreviewMode = computed(() => route.query['view-mode'] === '1')

Chart.register(...registerables)

const UPPER_LEFT_QUARTER_TEETH = [11, 12, 13, 14, 15, 16, 17, 18]
const UPPER_RIGHT_QUARTER_TEETH = [21, 22, 23, 24, 25, 26, 27, 28]
const DOWN_LEFT_QUARTER_TEETH = [31, 32, 33, 34, 35, 36, 37, 38]
const DOWN_RIGHT_QUARTER_TEETH = [41, 42, 43, 44, 45, 46, 47, 48]

const UP_FRONT_TEETH_ORDER = [
  ...UPPER_LEFT_QUARTER_TEETH.toReversed(),
  ...UPPER_RIGHT_QUARTER_TEETH,
]
const UP_BACK_TEETH_ORDER = UP_FRONT_TEETH_ORDER.toReversed()
const DOWN_FRONT_TEETH_ORDER = [
  ...DOWN_RIGHT_QUARTER_TEETH.toReversed(),
  ...DOWN_LEFT_QUARTER_TEETH,
]
const DOWN_BACK_TEETH_ORDER = DOWN_FRONT_TEETH_ORDER.toReversed()

const SORTER_ORDER_MAP = {
  up: {
    front: UP_FRONT_TEETH_ORDER,
    back: UP_BACK_TEETH_ORDER,
  },
  down: {
    front: DOWN_FRONT_TEETH_ORDER,
    back: DOWN_BACK_TEETH_ORDER,
  },
}

function getSortedArrayWithGivenOrder<Item>(
  sortOrder: number[],
  arr: Item[],
  getKeyFn: (item: Item) => string
): Item[] {
  const map: Map<string, Item> = new Map()

  for (const item of arr) {
    const key = String(getKeyFn(item))
    map.set(key, item)
  }

  const newArray: Item[] = []

  for (const key of sortOrder.map((item) => String(item))) {
    const item = map.get(key)

    if (!item) continue
    newArray.push(item)
  }

  return newArray
}

const teethList = computed(() => {
  const upper = props.isUpperTeeth
  const back = props.isReverse

  const verticalKey = upper ? 'up' : 'down'
  const horizontalKey = back ? 'back' : 'front'

  const sorterOrder = SORTER_ORDER_MAP[verticalKey][horizontalKey]
  const teethType = back ? DOWN_TEETH : UP_TEETH

  const getSorterKeyFn = (item: typeof UP_TEETH[number]) =>
    String(upper ? item.id[0] : item.id[1])

  return getSortedArrayWithGivenOrder(sorterOrder, teethType, getSorterKeyFn)
})

const recession = computed(() => {
  const items = props.data?.recession.map((item) => {
    if (item < -10) return 10

    if (item > 16) return -16

    return -item
  })

  if (!props.isReverse) {
    return items?.map((item) => item * -1)
  }

  return items
})

const previousRecession = computed(() => {
  const items = props.previousData?.recession.map((item) => {
    if (item < -10) return 10

    if (item > 16) return -16

    return -item
  })

  if (!isOverlay.value) {
    return items?.map(() => 0)
  }

  if (!props.isReverse) {
    return items?.map((item) => item * -1)
  }

  return items
})

const sounding = computed(() => {
  const computeSounding = props.data?.sounding?.map((soundingValue, index) => {
    const recessionValue = props.data?.recession[index]
    const sumOfValues = soundingValue + recessionValue
    return sumOfValues > 20 ? 20 : sumOfValues
  })
  if (props.isReverse) {
    return computeSounding?.map((item) => item * -1)
  }

  return computeSounding
})

const previousSounding = computed(() => {
  const computeSounding = props.previousData?.sounding?.map(
    (soundingValue, index) => {
      const recessionValue = props.previousData?.recession[index]
      return soundingValue + recessionValue
    }
  )
  if (!isOverlay.value) {
    return computeSounding?.map(() => 0)
  }

  if (props.isReverse) {
    return computeSounding?.map((item) => item * -1)
  }

  return computeSounding
})

const labels = computed(() => props.data?.recession)

const chartData = computed(() => {
  const datasets = [
    {
      data: recession.value,
      borderColor: '#fa8a8a',
      pointRadius: 0,
      spanGaps: true,
      borderWidth: 1,
    },
    {
      data: sounding.value,
      fill: '0',
      borderColor: '#696dce',
      pointRadius: 0,
      backgroundColor: 'rgba(105, 109, 206, 0.7)',
      borderWidth: 3,
    },
    {
      data: previousSounding.value,
      borderColor: 'green',
      pointRadius: 0,
      fill: '3',
      backgroundColor: 'rgba(0, 255, 0, 0.7)',
      borderWidth: 3,
    },
    {
      data: previousRecession.value,
      borderColor: 'green',
      pointRadius: 0,
      spanGaps: true,
      borderWidth: 1,
    },
  ]

  return {
    labels: labels.value,
    datasets,
  }
})

const displayYAxes = (currentChart) => {
  const context = currentChart.chart.ctx

  context.fillStyle = '#222'

  if (!isPreviewMode.value) {
    context.font = '9px Manrope'
  } else {
    context.font = '8px Manrope'
  }

  if (props.isReverse) {
    if (props.isInteractive) {
      fillLegendText(context, 43, 61, 78, 94, 114)
    } else if (isPreviewMode.value) {
      fillLegendText(context, 32, 45, 56, 67, 82)
    } else {
      fillLegendText(context, 36, 51, 63, 78, 94)
    }

    return
  }

  if (props.isInteractive) {
    fillLegendText(context, 80, 64, 46, 30, 10)
  } else if (isPreviewMode.value) {
    fillLegendText(context, 58, 46, 34, 22, 10)
  } else {
    fillLegendText(context, 66, 52, 39, 25, 10)
  }
}

const fillLegendText = (context: any, ...values: number[]): void => {
  context.fillText('0', 0, values[0])
  context.fillText('5', 0, values[1])
  context.fillText('10', 0, values[2])
  context.fillText('15', 0, values[3])
  context.fillText('20', 0, values[4])
}

const options = computed(() => ({
  events: [],
  hover: false,
  animation: {
    duration: 500,
    onComplete(currentChart) {
      displayYAxes(currentChart)
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
    },
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  scales: {
    x: {
      grid: {
        tickColor: 'transparent',
        drawOnChartArea: false,
      },
      ticks: {
        color: 'transparent',
      },
    },
    y: {
      min: props.isReverse ? -21 : -10,
      max: props.isReverse ? 10 : 21,
      ticks: {
        color: 'transparent',
        font: {
          size: 5,
        },
      },
    },
  },
}))

const checkIsToothRemoved = (toothItemIds: number[]): boolean => {
  return (
    props.data?.removedTooth.includes(toothItemIds[0]) ||
    props.data?.removedTooth.includes(toothItemIds[1])
  )
}

const checkIsImplant = (toothItemIds: number[]): boolean => {
  return (
    props.data?.implantTooth.includes(toothItemIds[0]) ||
    props.data?.implantTooth.includes(toothItemIds[1])
  )
}

const getToothWidth = (teeth: TeethType): number => {
  if (props.isInteractive) {
    return teeth.width + 10
  }

  return teeth.width
}

const getToothHeight = (teeth: TeethType): number => {
  if (props.isInteractive) {
    return teeth.height + 20
  }

  return teeth.height
}

const { lineChartProps } = useLineChart({
  // @ts-ignore
  chartData,
  options: options.value,
})

const instance = getCurrentInstance()
watch(
  () => isOverlay.value,
  () => {
    instance.ctx.$forceUpdate()
    instance?.proxy?.$forceUpdate()
  },
  { deep: true }
)

return (_ctx: any,_cache: any) => {
  const _component_icon_template = _resolveComponent("icon-template")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([{
      'periodontal-chart--interactive': __props.isInteractive,
      'periodontal-chart--preview': _unref(isPreviewMode),
    }, "periodontal-chart"])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", {
        class: _normalizeClass([{
          'periodontal-chart__teeth--down': __props.isReverse,
          'periodontal-chart__teeth--preview': _unref(isPreviewMode),
          'periodontal-chart__teeth--pdf': __props.isPdfMode,
          'periodontal-chart__teeth--interactive': __props.isInteractive,
        }, "periodontal-chart__teeth"])
      }, [
        (_unref(isPreviewMode) || __props.isPdfMode)
          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(_unref(teethList), (tooth, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: index,
                class: "periodontal-chart__tooth-wrapper periodontal-chart__tooth-wrapper--preview"
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass([{
                'periodontal-chart__tooth--removed': checkIsToothRemoved(
                  tooth.id
                ),
              }, "periodontal-chart__tooth periodontal-chart__tooth--preview"]),
                  innerHTML: checkIsImplant(tooth.id) ? tooth.implantSvg : tooth.svg
                }, null, 10, _hoisted_2)
              ]))
            }), 128))
          : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(_unref(teethList), (tooth, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: index,
                class: _normalizeClass([{
              'periodontal-chart__tooth-wrapper--interactive': __props.isInteractive,
            }, "periodontal-chart__tooth-wrapper"])
              }, [
                _createVNode(_component_icon_template, {
                  class: _normalizeClass([{
                'periodontal-chart__tooth--removed': checkIsToothRemoved(
                  tooth.id
                ),
              }, "periodontal-chart__tooth"]),
                  height: getToothHeight(tooth),
                  name: checkIsImplant(tooth.id) ? tooth.implantName : tooth.name,
                  width: getToothWidth(tooth)
                }, null, 8, ["class", "height", "name", "width"])
              ], 2))
            }), 128))
      ], 2),
      _createVNode(_unref(LineChart), _mergeProps({
        class: [{
          'periodontal-chart__chart--reverse': __props.isReverse,
          'periodontal-chart__chart--interactive': __props.isInteractive,
          'periodontal-chart__chart--preview': _unref(isPreviewMode),
        }, "periodontal-chart__chart"]
      }, _unref(lineChartProps)), null, 16, ["class"])
    ])
  ], 2))
}
}

})